import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";

/* VisitTracker · two-in-one analytics shim.
 *
 *   (1) PAGEVIEWS · sends one /track-visit beacon per route change with
 *       the path, referrer (only on the first nav of the session so
 *       internal navigation doesn't over-attribute), page title,
 *       viewport size and browser language.
 *
 *   (2) CTA CLICKS · installs a SINGLE global click listener that
 *       fires /track-event whenever the user clicks any element
 *       carrying `data-track="<label>"` OR any external <a target=…>
 *       link. We capture only meaningful CTAs — random text-spans don't
 *       qualify, otherwise the events collection floods.
 *
 * Both calls are fire-and-forget · we never block navigation. Tracking
 * is skipped on /admin/* so the admin dashboard doesn't pollute its
 * own analytics.
 */
let sentReferrer = false;
function getSessionId() {
  try {
    let s = sessionStorage.getItem("track_sid");
    if (!s) {
      s = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem("track_sid", s);
    }
    return s;
  } catch {
    return "anon";
  }
}

export default function VisitTracker() {
  const location = useLocation();

  // (1) Pageview tracking
  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;
    const ref = sentReferrer ? null : document.referrer || null;
    sentReferrer = true;
    api
      .post("/track-visit", {
        path: location.pathname + location.search,
        referrer: ref,
        title: document.title,
        screen: `${window.innerWidth}x${window.innerHeight}`,
        lang: navigator.language || null,
        session_id: getSessionId(),
      })
      .catch(() => { /* analytics non-critical */ });
  }, [location.pathname, location.search]);

  // (2) Global click listener · mounted once
  useEffect(() => {
    const handler = (e) => {
      if (window.location.pathname.startsWith("/admin")) return;
      const el = e.target.closest?.("[data-track], a, button");
      if (!el) return;
      const explicit = el.getAttribute("data-track");
      const isAnchor = el.tagName === "A";
      if (!explicit && !isAnchor) return;
      const href = isAnchor ? (el.getAttribute("href") || "") : "";
      // Skip in-page jumps · empty anchors · javascript:void(0)
      if (href.startsWith("#") || href.startsWith("javascript:") || (!explicit && !href)) return;

      const label =
        explicit ||
        el.getAttribute("data-cta") ||
        el.getAttribute("aria-label") ||
        (el.innerText || "").trim().slice(0, 80) ||
        href;
      const target = href || (el.getAttribute("data-target") || "");
      const external =
        isAnchor &&
        /^https?:\/\//.test(target) &&
        !target.includes(window.location.host);

      api
        .post("/track-event", {
          event: "click",
          label,
          target,
          external,
          page: window.location.pathname,
          session_id: getSessionId(),
        })
        .catch(() => {});
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, []);

  return null;
}
